import { ROUTES } from "../../../components/shared/constant";
import { NAV_IDS, PERF_NAV_IDS, PIT_NAV_IDS, HRIS_NAV_IDS, type NavId } from "./sidebarConfig";
import { skySurface, skySurfaceRoutes } from "../../skyagent/surfaces";
import type { NavSection } from "@skypointcloud/skypoint-ui";
import { isSharedPath } from "../../../routes/sharedPaths";
import { DEFAULT_PRODUCT, type ProductKey } from "../../../../shared/products/keys";

/** The products' nav ids: on a tie for one path, a hire item keeps the highlight over these. */
const PERFORMANCE_NAV_ID_SET: ReadonlySet<string> = new Set([
  ...Object.values(PERF_NAV_IDS),
  ...Object.values(PIT_NAV_IDS),
  ...Object.values(HRIS_NAV_IDS),
]);

/**
 * The tiled Settings pages (2026-10-08 shell spec, D5), one per product, each the route of that
 * product's one Settings item. Matched EXACTLY, never as a prefix: the hub opens to whoever is drawn
 * the Settings item, and a settings page beneath it (`/settings/locations`,
 * `/performance/settings/labels`) is admitted only by its own entry, exactly as before. That is why
 * they are not in ROUTE_PATTERNS, whose patterns admit everything beneath them.
 */
export const SETTINGS_HUB_ROUTES: Readonly<Partial<Record<NavId, string>>> = {
  [NAV_IDS.SETTINGS]: ROUTES.SETTINGS_HUB,
  [NAV_IDS.PERF_SETTINGS]: ROUTES.PERFORMANCE_SETTINGS,
};

/**
 * Every tiled page a sidebar item opens: each product's Settings, and Hire's Templates (Templates &
 * Setup). Each works exactly as Settings does (settingsHub.ts): the item's children are its entries,
 * the drawer draws it as one item, its page shows a card per entry, and it is matched EXACTLY, never
 * as a prefix (`/templates` never claims `/templates/email`, which its own entry admits).
 */
export const HUB_ROUTES: Readonly<Partial<Record<NavId, string>>> = {
  ...SETTINGS_HUB_ROUTES,
  [NAV_IDS.TEMPLATES]: ROUTES.TEMPLATES_HUB,
};

/** Whether `navId` is a product's one Settings item, the item whose children are tiles. */
export function isSettingsHubId(navId: string | undefined): boolean {
  return navId !== undefined && navId in SETTINGS_HUB_ROUTES;
}

/** Whether `navId` is an item opening a tiled page (HUB_ROUTES): a Settings item, or Templates. */
export function isHubId(navId: string | undefined): boolean {
  return navId !== undefined && navId in HUB_ROUTES;
}

/** The hub item whose tiled page is exactly `pathname` (the query string ignored), if any. */
function settingsHubAt(pathname: string, queryString = ""): NavId | undefined {
  const path =
    queryString && pathname.endsWith(queryString)
      ? pathname.slice(0, -queryString.length)
      : pathname;
  return (Object.entries(HUB_ROUTES) as [NavId, string][]).find(([, route]) => route === path)?.[0];
}

export function getNavRoute(navId: NavId): string {
  return NAV_TO_ROUTE_BASE[navId];
}

export function getAllNavRoutes(navId: NavId): string[] {
  return ROUTE_PATTERNS[navId] ?? [];
}

const NAV_TO_ROUTE_BASE: Record<NavId, string> = {
  [NAV_IDS.DASHBOARD]: ROUTES.HOME,
  [NAV_IDS.REFERRAL_HUB]: ROUTES.REFERRAL_HUB,
  [NAV_IDS.REFERRAL_OPEN_ROLES]: ROUTES.REFERRAL_OPEN_ROLES,
  [NAV_IDS.MY_REFERRALS]: ROUTES.MY_REFERRALS,
  [NAV_IDS.REFERRAL_REWARDS]: ROUTES.REFERRAL_REWARDS,
  [NAV_IDS.APPLICANTS]: ROUTES.APPLICANTS,
  [NAV_IDS.CANDIDATE_INBOX]: ROUTES.CANDIDATE_INBOX,
  [NAV_IDS.BULK_UPLOADS]: ROUTES.BULK_UPLOADS,
  [NAV_IDS.JOBS]: ROUTES.JOBS,
  [NAV_IDS.TALENT_REDISCOVERY]: ROUTES.TALENT_REDISCOVERY,
  [NAV_IDS.DOCUMENT_CENTRE]: ROUTES.DOCUMENT_CENTRE,
  [NAV_IDS.SCHEDULED_TOURS]: ROUTES.SCHEDULED_TOURS,
  [NAV_IDS.SURVEYS]: ROUTES.SURVEYS,
  [NAV_IDS.CAREER_PORTALS]: ROUTES.JOB_PORTALS,
  // Both sections read their href from the surface catalog. Listed key by key
  // rather than spread, so this map keeps failing to compile when a surface is
  // added there and not routed here — a spread would satisfy Record<NavId, …>
  // with an index signature and quietly accept a missing entry.
  [NAV_IDS.SKYAGENT_WORKSPACE]: skySurface("workspace").href,
  [NAV_IDS.SKYAGENT_AI_EMPLOYEES]: skySurface("ai-employees").href,
  [NAV_IDS.SKYAGENT_APPROVALS]: skySurface("approvals").href,
  [NAV_IDS.SKYAGENT_ARTIFACTS]: skySurface("artifacts").href,
  [NAV_IDS.SKYAGENT_CONFIGURATION]: skySurface("configuration").href,
  [NAV_IDS.SKYBRAIN_OVERVIEW]: skySurface("overview").href,
  [NAV_IDS.SKYBRAIN_MEMORY]: skySurface("memory").href,
  [NAV_IDS.SKYBRAIN_SKILLS]: skySurface("skills").href,
  [NAV_IDS.SKYBRAIN_QUICK_PROMPTS]: skySurface("quick-prompts").href,
  // D5: the one Settings item opens the tiled Settings page.
  [NAV_IDS.SETTINGS]: ROUTES.SETTINGS_HUB,
  [NAV_IDS.GENERAL]: ROUTES.GENERAL_SETTINGS,
  [NAV_IDS.LOCATIONS]: ROUTES.LOCATIONS,
  [NAV_IDS.DEPARTMENTS]: ROUTES.DEPARTMENTS_SETTINGS,
  [NAV_IDS.USERS]: ROUTES.ACCESS_MANAGEMENT,
  [NAV_IDS.ROLES]: ROUTES.ACCESS_MANAGEMENT,
  [NAV_IDS.ACCESS_MANAGEMENT]: ROUTES.ACCESS_MANAGEMENT,
  [NAV_IDS.ROLE_MANAGEMENT]: ROUTES.ROLE_MANAGEMENT,
  [NAV_IDS.CLIENTS_MANAGEMENT]: ROUTES.CLIENTS_MANAGEMENT,
  [NAV_IDS.LOGS]: ROUTES.LOGS,
  [NAV_IDS.PLATFORM]: ROUTES.PLATFORM,
  [NAV_IDS.INSTANCE_PRODUCTS]: ROUTES.INSTANCE_PRODUCTS,
  [NAV_IDS.TENANTS]: ROUTES.TENANTSETIINGS,
  [NAV_IDS.INTEGRATIONS]: ROUTES.CRM_INTEGRATIONS,
  [NAV_IDS.EMAIL_TEMPLATES]: ROUTES.EMAIL_TEMPLATES,
  [NAV_IDS.SMS_TEMPLATES]: ROUTES.SMS_TEMPLATES,
  [NAV_IDS.EMAIL_SIGNATURE]: ROUTES.EMAIL_SIGNATURE,
  [NAV_IDS.SENDER_EMAILS]: ROUTES.SENDER_EMAILS,
  [NAV_IDS.FEEDBACK_TEMPLATES]: ROUTES.FEEDBACK_TEMPLATES,
  [NAV_IDS.AI_INTERVIEW_TEMPLATES]: ROUTES.AI_INTERVIEW_TEMPLATES,
  [NAV_IDS.INTERVIEW_GUIDES]: ROUTES.INTERVIEW_GUIDES,
  [NAV_IDS.PIPELINE]: ROUTES.PIPELINE_SETTINGS,
  [NAV_IDS.AI_SCORING]: ROUTES.AI_SCORING,
  [NAV_IDS.ARCHIVE_REASONS]: ROUTES.ARCHIVE_REASONS_SETTINGS,
  [NAV_IDS.CANDIDATE_ENGAGEMENT]: ROUTES.CANDIDATE_ENGAGEMENT_SETTINGS,
  [NAV_IDS.JOB_POSTING_OPTIONS]: ROUTES.JOB_POSTING_OPTIONS_SETTINGS,
  [NAV_IDS.JOB_TITLES]: ROUTES.JOB_TITLES_SETTINGS,
  [NAV_IDS.HELP_PLUS_SUPPORT]: ROUTES.HELP_PLUS_SUPPORT,
  [NAV_IDS.CANDIDATE_CONTACT_INFO]: ROUTES.CANDIDATE_CONTACT_INFO,
  [NAV_IDS.RESUME_DIGEST]: ROUTES.RESUME_DIGEST,
  [NAV_IDS.RESUME_TEMPLATES]: ROUTES.RESUME_TEMPLATES,
  [NAV_IDS.ANONYMIZATION_CONFIGS]: ROUTES.ANONYMIZATION_CONFIGS,
  [NAV_IDS.REPORTS]: ROUTES.REPORTS,
  [NAV_IDS.OFFER_TEMPLATES]: ROUTES.OFFER_TEMPLATES,
  [NAV_IDS.OFFER_WORKFLOWS]: ROUTES.OFFER_WORKFLOWS,
  // The one Templates item opens the tiled Templates page.
  [NAV_IDS.TEMPLATES]: ROUTES.TEMPLATES_HUB,
  // skyPerformance. D5: the one Settings item opens the tiled Settings page; E9's tabbed page is
  // one of its entries (PERF_CONFIG), beneath it.
  [NAV_IDS.PERF_HOME]: ROUTES.PERFORMANCE,
  // skyPerformance IT (spec 2026-10-26 §4.6): Home is its landing.
  [NAV_IDS.PIT_HOME]: ROUTES.PERFORMANCE_IT,
  [NAV_IDS.HRIS_ONBOARDING]: ROUTES.ONBOARDING,
  [NAV_IDS.HRIS_OFFBOARDING]: ROUTES.OFFBOARDING,
  [NAV_IDS.HRIS_APPROVALS]: ROUTES.ONBOARDING_APPROVALS,
  [NAV_IDS.HRIS_SETTINGS]: ROUTES.ONBOARDING_SETTINGS,
  [NAV_IDS.PERF_SETTINGS]: ROUTES.PERFORMANCE_SETTINGS,
  [NAV_IDS.PERF_CONFIG]: ROUTES.PERFORMANCE_SETTINGS,
  // Shipped (SHIPPED_FEATURES): the coaching log. Its record pages sit below it, so the prefix
  // match in ROUTE_PATTERNS and the guard's isUnder cover /performance/coaching/records/:recordId.
  [NAV_IDS.PERF_COACHING]: ROUTES.PERFORMANCE_COACHING,
  // E2: the prefix covers /performance/file/:personKey, as coaching's covers its record pages.
  [NAV_IDS.PERF_FILE]: ROUTES.PERFORMANCE_FILE,
  // Phase 3 placeholders: not in SHIPPED_FEATURES, so never drawn and never admitted by the guard.
  [NAV_IDS.PERF_TODOS]: ROUTES.PERFORMANCE_TODOS,
  [NAV_IDS.PERF_PLANS]: ROUTES.PERFORMANCE_PLANS,
  [NAV_IDS.PERF_EVALUATIONS]: ROUTES.PERFORMANCE_EVALUATIONS,
  [NAV_IDS.PERF_VISITS]: ROUTES.PERFORMANCE_VISITS,
  [NAV_IDS.PERF_ORG]: ROUTES.PERFORMANCE_ORG,
  [NAV_IDS.PERF_RECORDS]: ROUTES.PERFORMANCE_RECORDS,
  [NAV_IDS.PERF_DOCUMENTS]: ROUTES.PERFORMANCE_DOCUMENTS,
};

const ROUTE_PATTERNS: Record<NavId, string[]> = {
  [NAV_IDS.DASHBOARD]: [ROUTES.HOME, ROUTES.DASHBOARD],
  [NAV_IDS.REFERRAL_HUB]: [ROUTES.REFERRAL_HUB],
  [NAV_IDS.REFERRAL_OPEN_ROLES]: [ROUTES.REFERRAL_OPEN_ROLES],
  [NAV_IDS.MY_REFERRALS]: [ROUTES.MY_REFERRALS],
  [NAV_IDS.REFERRAL_REWARDS]: [ROUTES.REFERRAL_REWARDS],
  [NAV_IDS.APPLICANTS]: [ROUTES.APPLICANTS, ROUTES.APPLICANT_DETAIL, ROUTES.BULK_UPLOADS],
  [NAV_IDS.CANDIDATE_INBOX]: [ROUTES.CANDIDATE_INBOX],
  [NAV_IDS.BULK_UPLOADS]: [ROUTES.BULK_UPLOADS],
  [NAV_IDS.JOBS]: [ROUTES.JOBS, ROUTES.JOB_CREATE, ROUTES.JOB_DETAIL],
  [NAV_IDS.TALENT_REDISCOVERY]: [ROUTES.TALENT_REDISCOVERY],
  // Also admits one candidate's folder, /document-centre/:applicationId, beneath it.
  [NAV_IDS.DOCUMENT_CENTRE]: [ROUTES.DOCUMENT_CENTRE],
  [NAV_IDS.SCHEDULED_TOURS]: [ROUTES.SCHEDULED_TOURS],
  [NAV_IDS.SURVEYS]: [
    ROUTES.SURVEYS,
    ROUTES.SURVEY_CREATE,
    ROUTES.SURVEY_DETAIL,
    ROUTES.SURVEY_EDIT,
    ROUTES.SURVEY_PREVIEW,
    ROUTES.SURVEY_RESULTS,
    ROUTES.SURVEY_RESULTS_DETAIL,
    ROUTES.SURVEY_REQUESTS,
  ],
  [NAV_IDS.CAREER_PORTALS]: [ROUTES.JOB_PORTALS, ROUTES.JOB_PORTAL_EDIT],
  // skySurfaceRoutes returns the surface's own path plus the retired ones that
  // redirect to it. The retired paths have to be here: AppLayout's route guard
  // builds its allow-list from this map, so a path it does not list is bounced
  // to Applicants before its redirect can render.
  [NAV_IDS.SKYAGENT_WORKSPACE]: skySurfaceRoutes("workspace"),
  [NAV_IDS.SKYAGENT_AI_EMPLOYEES]: skySurfaceRoutes("ai-employees"),
  [NAV_IDS.SKYAGENT_APPROVALS]: skySurfaceRoutes("approvals"),
  [NAV_IDS.SKYAGENT_ARTIFACTS]: skySurfaceRoutes("artifacts"),
  [NAV_IDS.SKYAGENT_CONFIGURATION]: skySurfaceRoutes("configuration"),
  // Overview also carries Quick Prompts, which has no nav row of its own.
  // AppLayout builds the route guard's allow-list from sidebar entries, so
  // without this /skybrain/quick-prompts is bounced to Applicants and the
  // Overview's own link to it would not open. It also keeps Overview
  // highlighted while that page is open, which is where it is reached from.
  [NAV_IDS.SKYBRAIN_OVERVIEW]: [
    ...skySurfaceRoutes("overview"),
    ...skySurfaceRoutes("quick-prompts"),
  ],
  [NAV_IDS.SKYBRAIN_MEMORY]: skySurfaceRoutes("memory"),
  [NAV_IDS.SKYBRAIN_SKILLS]: skySurfaceRoutes("skills"),
  // No nav row: reached from Overview, whose patterns list it above.
  [NAV_IDS.SKYBRAIN_QUICK_PROMPTS]: [],
  // The tiled page is matched exactly, in SETTINGS_HUB_ROUTES, never as a prefix.
  [NAV_IDS.SETTINGS]: [],
  [NAV_IDS.GENERAL]: [ROUTES.GENERAL_SETTINGS],
  [NAV_IDS.LOCATIONS]: [ROUTES.LOCATIONS],
  [NAV_IDS.DEPARTMENTS]: [ROUTES.DEPARTMENTS_SETTINGS],
  [NAV_IDS.USERS]: [ROUTES.ACCESS_MANAGEMENT],
  [NAV_IDS.ROLES]: [ROUTES.ACCESS_MANAGEMENT],
  [NAV_IDS.ACCESS_MANAGEMENT]: [ROUTES.ACCESS_MANAGEMENT],
  [NAV_IDS.ROLE_MANAGEMENT]: [ROUTES.ROLE_MANAGEMENT],
  [NAV_IDS.CLIENTS_MANAGEMENT]: [ROUTES.CLIENTS_MANAGEMENT],
  [NAV_IDS.LOGS]: [ROUTES.LOGS],
  [NAV_IDS.PLATFORM]: [ROUTES.PLATFORM],
  // Drawn for platform admins only, so the guard admits it to nobody else.
  [NAV_IDS.INSTANCE_PRODUCTS]: [ROUTES.INSTANCE_PRODUCTS],
  [NAV_IDS.TENANTS]: [ROUTES.TENANTSETIINGS, ROUTES.CREATETENANT],
  [NAV_IDS.INTEGRATIONS]: [
    ROUTES.CRM_INTEGRATIONS,
    ROUTES.INDEED_INTEGRATION,
    ROUTES.LINKEDIN_INTEGRATION,
    ROUTES.HRIS_INTEGRATION,
    ROUTES.GREYTHR_INTEGRATION,
    // The greytHR review page has no sidebar entry of its own, and the route guard's allow-list is
    // built from the sidebar: without this it is not admitted, so "Create greytHR profile" saves
    // the hire and then lands on the dashboard. It belongs under Integrations because that is the
    // permission that may approve a push.
    ROUTES.GREYTHR_REVIEW_BASE,
    ROUTES.CONNECTORS,
  ],
  // One nav entry now covers both, so the SMS route has to keep it highlighted
  // too, otherwise switching the heading to SMS visibly deselects the sidebar.
  [NAV_IDS.EMAIL_TEMPLATES]: [ROUTES.EMAIL_TEMPLATES, ROUTES.SMS_TEMPLATES],
  [NAV_IDS.SMS_TEMPLATES]: [ROUTES.SMS_TEMPLATES],
  [NAV_IDS.EMAIL_SIGNATURE]: [ROUTES.EMAIL_SIGNATURE],
  [NAV_IDS.SENDER_EMAILS]: [ROUTES.SENDER_EMAILS],
  [NAV_IDS.FEEDBACK_TEMPLATES]: [ROUTES.FEEDBACK_TEMPLATES],
  [NAV_IDS.AI_INTERVIEW_TEMPLATES]: [ROUTES.AI_INTERVIEW_TEMPLATES],
  [NAV_IDS.INTERVIEW_GUIDES]: [ROUTES.INTERVIEW_GUIDES],
  [NAV_IDS.PIPELINE]: [ROUTES.PIPELINE_SETTINGS],
  [NAV_IDS.AI_SCORING]: [ROUTES.AI_SCORING],
  [NAV_IDS.ARCHIVE_REASONS]: [ROUTES.ARCHIVE_REASONS_SETTINGS],
  // Both tab URLs keep the one nav entry highlighted.
  [NAV_IDS.CANDIDATE_ENGAGEMENT]: [
    ROUTES.CANDIDATE_ENGAGEMENT_SETTINGS,
    ROUTES.CANDIDATE_ENGAGEMENT_RULES,
  ],
  [NAV_IDS.JOB_POSTING_OPTIONS]: [ROUTES.JOB_POSTING_OPTIONS_SETTINGS],
  [NAV_IDS.JOB_TITLES]: [ROUTES.JOB_TITLES_SETTINGS],
  [NAV_IDS.HELP_PLUS_SUPPORT]: [ROUTES.HELP_PLUS_SUPPORT],
  [NAV_IDS.CANDIDATE_CONTACT_INFO]: [ROUTES.CANDIDATE_CONTACT_INFO],
  [NAV_IDS.RESUME_DIGEST]: [ROUTES.RESUME_DIGEST],
  [NAV_IDS.RESUME_TEMPLATES]: [ROUTES.RESUME_TEMPLATES],
  [NAV_IDS.ANONYMIZATION_CONFIGS]: [ROUTES.ANONYMIZATION_CONFIGS],
  [NAV_IDS.REPORTS]: [ROUTES.REPORTS],
  [NAV_IDS.OFFER_TEMPLATES]: [ROUTES.OFFER_TEMPLATES, ROUTES.OFFER_TEMPLATE_EDIT],
  [NAV_IDS.OFFER_WORKFLOWS]: [ROUTES.OFFER_WORKFLOWS],
  // The tiled page is matched exactly, in HUB_ROUTES, never as a prefix: every template page is
  // admitted and owned by its own entry, a child of this item.
  [NAV_IDS.TEMPLATES]: [],
  // skyPerformance. /performance is a prefix of every performance route; the longest match in
  // getSelectedNavItem still prefers the child, and the guard admits only what the sidebar draws.
  // E3.b R-A (spec §4.6, core.js:266): the subject's own plan sits under Home, so an employee, who
  // draws no Plans item, opens it while every other plan page stays Plans' alone (longest match).
  // E4: likewise the subject's own completed evaluation (no Evaluations item for an employee).
  // skyPerformance IT: /it-performance and, until later phases add their own items, nothing else.
  [NAV_IDS.PIT_HOME]: [ROUTES.PERFORMANCE_IT],
  // HRIS. /hris is a prefix of the others, so the longest match gives each page to its own item;
  // the case page (/hris/cases/:id) falls to Onboarding.
  [NAV_IDS.HRIS_ONBOARDING]: [ROUTES.ONBOARDING],
  [NAV_IDS.HRIS_OFFBOARDING]: [ROUTES.OFFBOARDING],
  [NAV_IDS.HRIS_APPROVALS]: [ROUTES.ONBOARDING_APPROVALS],
  [NAV_IDS.HRIS_SETTINGS]: [ROUTES.ONBOARDING_SETTINGS],
  [NAV_IDS.PERF_HOME]: [
    ROUTES.PERFORMANCE,
    ROUTES.PERFORMANCE_PLANS_MY,
    ROUTES.PERFORMANCE_EVALUATIONS_MY,
  ],
  // The tiled page is matched exactly, in SETTINGS_HUB_ROUTES, never as a prefix.
  [NAV_IDS.PERF_SETTINGS]: [],
  // E9: every tab of the tabbed Settings page, Positions included. D5: the shared Users page is an
  // entry of its own under the Settings item (access-management), so this no longer claims it.
  [NAV_IDS.PERF_CONFIG]: [ROUTES.PERFORMANCE_SETTINGS],
  [NAV_IDS.PERF_COACHING]: [ROUTES.PERFORMANCE_COACHING],
  [NAV_IDS.PERF_FILE]: [ROUTES.PERFORMANCE_FILE],
  [NAV_IDS.PERF_TODOS]: [ROUTES.PERFORMANCE_TODOS],
  [NAV_IDS.PERF_PLANS]: [ROUTES.PERFORMANCE_PLANS],
  [NAV_IDS.PERF_EVALUATIONS]: [ROUTES.PERFORMANCE_EVALUATIONS],
  [NAV_IDS.PERF_VISITS]: [ROUTES.PERFORMANCE_VISITS],
  // E2 O-1 (operator-approved 2026-10-06): a manager and HR draw no My file, so the file pages
  // also belong to the Org chart (the wireframe's file is #/org/:id), as E9 put Users under
  // Settings. The tie with My file goes to whichever of the two the shell draws; in hire's shell
  // and for an employee, to My file, declared first.
  [NAV_IDS.PERF_ORG]: [ROUTES.PERFORMANCE_ORG, ROUTES.PERFORMANCE_FILE],
  [NAV_IDS.PERF_RECORDS]: [ROUTES.PERFORMANCE_RECORDS],
  [NAV_IDS.PERF_DOCUMENTS]: [ROUTES.PERFORMANCE_DOCUMENTS],
};

export function getSelectedNavItem(
  pathname: string,
  queryString: string = "",
): {
  selectedId: string | undefined;
  selectedCategoryId: string | undefined;
} {
  // A tiled page lights its item: Settings (D5), or Templates.
  const hub = settingsHubAt(pathname, queryString);
  if (hub) return { selectedId: hub, selectedCategoryId: undefined };

  const bestNavId = longestMatchingNavId(pathname, queryString);

  if (bestNavId) {
    return {
      selectedId: bestNavId,
      selectedCategoryId: getParentCategoryId(bestNavId as NavId),
    };
  }

  return {
    selectedId: NAV_IDS.DASHBOARD,
    selectedCategoryId: undefined,
  };
}

/**
 * The nav id whose route pattern matches `pathname` most specifically, among the ids `admit`
 * accepts (every id when absent). The longest pattern wins so child routes (e.g. /skyagent/memory)
 * beat their parent prefix (/skyagent) regardless of declaration order; on a tie the id declared
 * first keeps it.
 */
function longestMatchingNavId(
  pathname: string,
  queryString: string,
  admit?: (navId: string) => boolean,
): string | undefined {
  let bestNavId: string | undefined;
  let bestPattern = "";

  for (const [navId, patterns] of Object.entries(ROUTE_PATTERNS)) {
    if (admit && !admit(navId)) continue;
    for (const pattern of patterns) {
      const routeWithQuery = pattern + queryString;
      const isExact = pathname === pattern || pathname === routeWithQuery;
      const isPrefix = pathname.startsWith(pattern + "/") || pathname.startsWith(pattern + "?");
      if (!isExact && !isPrefix) continue;
      // Longest wins. On a tie, a hire item beats a performance item explicitly (PR #1314 review
      // P2-1): performance's Settings also claims the shared Users page, and hire's highlight
      // there must not depend on which entry is declared first.
      const longer = pattern.length > bestPattern.length;
      const hireWinsTie =
        pattern.length === bestPattern.length &&
        bestNavId !== undefined &&
        PERFORMANCE_NAV_ID_SET.has(bestNavId) &&
        !PERFORMANCE_NAV_ID_SET.has(navId);
      if (longer || hireWinsTie) {
        bestNavId = navId;
        bestPattern = pattern;
      }
    }
  }
  return bestNavId;
}

/**
 * Every nav id owning `pathname` through its most specific pattern, in declaration order: the
 * ids tied with getSelectedNavItem's pick. More than one only where a page is deliberately shared
 * (E2 O-1: the file pages, My file and the Org chart).
 */
export function navIdsOwning(pathname: string, queryString = ""): string[] {
  const owners: string[] = [];
  let best = "";
  for (const [navId, patterns] of Object.entries(ROUTE_PATTERNS)) {
    for (const pattern of patterns) {
      const isExact = pathname === pattern || pathname === pattern + queryString;
      const isPrefix = pathname.startsWith(pattern + "/") || pathname.startsWith(pattern + "?");
      if (!isExact && !isPrefix) continue;
      if (pattern.length > best.length) {
        best = pattern;
        owners.length = 0;
        owners.push(navId);
      } else if (pattern.length === best.length && !owners.includes(navId)) {
        owners.push(navId);
      }
    }
  }
  // A tiled page belongs to its item too (D5: Settings; and Templates), matched exactly; the entry whose
  // pattern also matches it (E9's tabbed page, PERF_CONFIG) still owns it, as before.
  const hub = settingsHubAt(pathname, queryString);
  if (hub && !owners.includes(hub)) owners.push(hub);
  return owners;
}

/** The drawn item whose children include `navId`: the group the drawer must open for it. */
function drawnParentOf(sections: readonly NavSection[], navId: string | undefined) {
  if (!navId) return undefined;
  return sections
    .flatMap((section) => section.items)
    .find((item) => item.children?.some((child) => child.id === navId))?.id;
}

/** Every nav id the sections draw, children included. */
function drawnIdsOf(sections: readonly NavSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    for (const item of section.items) {
      ids.add(item.id);
      for (const child of item.children ?? []) ids.add(child.id);
    }
  }
  return ids;
}

/**
 * The item to highlight, and the group holding it, in the shell of `activeProduct` drawing
 * `sections`.
 *
 * - Hire's shell: exactly getSelectedNavItem, as it has always been.
 * - Another product's shell: its own pages highlight as getSelectedNavItem picks them. A shared page
 *   (SHARED_PATHS, the one list) is matched only against the items this shell draws, so it lights
 *   the shell's own entry for it: `/settings/access-management` lights performance's Users. Several
 *   hire ids share that path (USERS, ROLES, ACCESS_MANAGEMENT) and the unfiltered match picks the
 *   first, `users`, which neither sidebar draws. A shared page the shell draws nothing for keeps
 *   getSelectedNavItem's answer.
 * - Either way outside hire, the group opened is the drawn group holding the item (none for an item
 *   drawn at the top level).
 */
export function getSelectedNavItemInShell(
  pathname: string,
  queryString: string,
  activeProduct: ProductKey,
  sections: readonly NavSection[],
): {
  selectedId: string | undefined;
  selectedCategoryId: string | undefined;
} {
  const selected = getSelectedNavItem(pathname, queryString);
  if (activeProduct === DEFAULT_PRODUCT) return selected;

  let selectedId = selected.selectedId;
  const drawn = drawnIdsOf(sections);
  // A page owned by more than one item (E2 O-1) lights the one this shell draws.
  if (selectedId && !drawn.has(selectedId)) {
    selectedId = navIdsOwning(pathname, queryString).find((id) => drawn.has(id)) ?? selectedId;
  }
  if (isSharedPath(pathname)) {
    selectedId = longestMatchingNavId(pathname, queryString, (id) => drawn.has(id)) ?? selectedId;
  }
  return {
    selectedId,
    selectedCategoryId:
      drawnParentOf(sections, selectedId) ??
      (selectedId === selected.selectedId ? selected.selectedCategoryId : undefined),
  };
}

function getParentCategoryId(navId: NavId): string | undefined {
  const settingsSubmenu: NavId[] = [
    NAV_IDS.GENERAL,
    NAV_IDS.LOCATIONS,
    NAV_IDS.DEPARTMENTS,
    NAV_IDS.USERS,
    NAV_IDS.ROLES,
    NAV_IDS.ACCESS_MANAGEMENT,
    NAV_IDS.ROLE_MANAGEMENT,
    NAV_IDS.CLIENTS_MANAGEMENT,
    NAV_IDS.LOGS,
    NAV_IDS.PLATFORM,
    NAV_IDS.INSTANCE_PRODUCTS,
    NAV_IDS.TENANTS,
    NAV_IDS.INTEGRATIONS,
    NAV_IDS.PIPELINE,
    NAV_IDS.ARCHIVE_REASONS,
    NAV_IDS.CANDIDATE_ENGAGEMENT,
    NAV_IDS.JOB_POSTING_OPTIONS,
    NAV_IDS.JOB_TITLES,
    NAV_IDS.CANDIDATE_CONTACT_INFO,
    NAV_IDS.RESUME_DIGEST,
    NAV_IDS.ANONYMIZATION_CONFIGS,
  ];

  if (settingsSubmenu.includes(navId)) {
    return NAV_IDS.SETTINGS;
  }

  return undefined;
}
